import { omit } from 'lodash-es';
import { OutstandingSuggestion } from './outstandingSuggestions';
import {
  BULK_REJECT_TRIGGER,
  OutstandingSuggestionsModalEvents,
  OutstandingSuggestionsModalStep,
  sendOutstandingSuggestionsModalEvent,
} from './outstandingSuggestionsTracking';
import { SuggestionStatus } from './suggestionStatusStorage';
import { sendEvent } from 'util/pendoTrack';

export type ExtractionTool = 'extraction1' | 'extraction2';

export type SuggestionTrigger = 'input' | 'button' | typeof BULK_REJECT_TRIGGER;
export type SuggestionDecision = 'accepted' | 'rejected';

export const autoDocExtEventName = (
  extractionTool: ExtractionTool,
  suffix: string
): string => `covidence.review.${extractionTool}.${suffix}`;

export const trackingEvents = (extractionTool: ExtractionTool) => {
  const name = (suffix: string) => autoDocExtEventName(extractionTool, suffix);
  const outstandingSuggestionsModal: OutstandingSuggestionsModalEvents = {
    viewed: name('outstanding_suggestions_modal_viewed'),
    dismissed: name('outstanding_suggestions_modal_dismissed'),
    reject_all: name('outstanding_suggestions_modal_reject_all'),
    review: name('outstanding_suggestions_modal_review'),
  };

  return {
    suggestionDecision: name('metadata_suggestion'),
    suggestionViewed: name('metadata_suggestion_viewed'),
    suggestionNotDisplayable: name('metadata_suggestion_not_displayable'),
    fieldRendered: name('metadata_field_rendered'),
    outstandingSuggestionsModal,
    quoteDisplayed: name('suggestion_quote_displayed'),
    quoteClicked: name('suggestion_quote_clicked'),
    quoteSearched: name('suggestion_quote_searched'),
    quoteSearchError: name('suggestion_quote_search_error'),
    // Intervention groups only exist on DE1
    interventionGroupSuggestionsDisplayed: name(
      'intervention_group_suggestions_displayed'
    ),
    interventionGroupSuggestionLinked: name(
      'intervention_group_suggestion_linked'
    ),
  };
};

type DE1Surface = {
  extractionTool: 'extraction1';
  doi: string | null;
};

type DE2Surface = {
  extractionTool: 'extraction2';
  field_alias: string;
  field_type?: string;
  suggestion_label?: string | null;
};

type CommonSuggestionData = {
  field: string;
  review_id: string;
  study_id: number;
  document_id: number | null | undefined;
  suggestion?: string | null;
};

export type DE1SuggestionTrackingData = CommonSuggestionData & DE1Surface;
export type DE2SuggestionTrackingData = CommonSuggestionData & DE2Surface;

export type SuggestionTrackingData =
  | DE1SuggestionTrackingData
  | DE2SuggestionTrackingData;

type RenderFlags = {
  has_suggestion: boolean;
  has_content: boolean;
  suggestions_enabled: boolean;
  suggestion_rejected: boolean;
};

export type DE1FieldRenderedTrackingData = DE1SuggestionTrackingData &
  RenderFlags;
export type DE2FieldRenderedTrackingData = DE2SuggestionTrackingData & {
  field_type: string;
} & RenderFlags;

export type FieldRenderedTrackingData =
  | DE1FieldRenderedTrackingData
  | DE2FieldRenderedTrackingData;

type ModalContext = {
  review_id: string;
  study_id: number;
  document_id: number | null;
};

export type DE1ModalTrackingContext = ModalContext & DE1Surface;
export type DE2ModalTrackingContext = ModalContext &
  Pick<DE2Surface, 'extractionTool'>;

export type ModalTrackingContext =
  | DE1ModalTrackingContext
  | DE2ModalTrackingContext;

const commonFields = ({
  field,
  review_id,
  study_id,
  document_id,
  suggestion,
}: CommonSuggestionData) => ({
  field,
  review_id,
  study_id,
  document_id,
  suggestion,
  path: window.location.pathname,
});

const surfaceFields = (data: SuggestionTrackingData) =>
  data.extractionTool === 'extraction1'
    ? { doi: data.doi }
    : {
        field_alias: data.field_alias,
        suggestion_label: data.suggestion_label,
      };

const fieldRenderedSurfaceFields = (data: FieldRenderedTrackingData) =>
  data.extractionTool === 'extraction1'
    ? { doi: data.doi }
    : { field_alias: data.field_alias, field_type: data.field_type };

export const trackSuggestionViewed = (data: SuggestionTrackingData) =>
  sendEvent(trackingEvents(data.extractionTool).suggestionViewed, {
    ...commonFields(data),
    ...surfaceFields(data),
  });

export const trackSuggestionNotDisplayable = (
  data: DE2SuggestionTrackingData
) =>
  sendEvent(trackingEvents(data.extractionTool).suggestionNotDisplayable, {
    ...commonFields(data),
    field_alias: data.field_alias,
  });

export const trackSuggestionDecision = (
  status: SuggestionDecision,
  data: SuggestionTrackingData & { trigger: SuggestionTrigger }
) =>
  sendEvent(trackingEvents(data.extractionTool).suggestionDecision, {
    ...commonFields(data),
    ...surfaceFields(data),
    status,
    trigger: data.trigger,
  });

export const trackFieldRendered = (data: FieldRenderedTrackingData) =>
  sendEvent(trackingEvents(data.extractionTool).fieldRendered, {
    ...commonFields(data),
    ...fieldRenderedSurfaceFields(data),
    has_suggestion: data.has_suggestion,
    has_content: data.has_content,
    suggestions_enabled: data.suggestions_enabled,
    suggestion_rejected: data.suggestion_rejected,
  });

export const trackOutstandingSuggestionsModal = (
  step: OutstandingSuggestionsModalStep,
  outstanding: OutstandingSuggestion[],
  context: ModalTrackingContext
): void =>
  sendOutstandingSuggestionsModalEvent(
    trackingEvents(context.extractionTool).outstandingSuggestionsModal[step],
    outstanding,
    omit(context, 'extractionTool')
  );

export type QuoteOpenTrigger = 'button' | 'auto';

export type QuoteTrackingData = {
  extractionTool: ExtractionTool;
  field: string;
  quotesCount: number;
  documentId: number | null | undefined;
  reviewStudyId: number;
  resultsCount?: number;
  quote?: string;
  suggestionStatus?: SuggestionStatus;
  trigger?: QuoteOpenTrigger;
};

const sendQuoteEvent = (eventName: string, data: QuoteTrackingData) =>
  sendEvent(eventName, {
    path: window.location.pathname,
    ...omit(data, 'extractionTool'),
  });

export const trackQuoteDisplayed = (data: QuoteTrackingData) =>
  sendQuoteEvent(trackingEvents(data.extractionTool).quoteDisplayed, data);

export const trackQuoteClicked = (data: QuoteTrackingData) =>
  sendQuoteEvent(trackingEvents(data.extractionTool).quoteClicked, data);

export const trackQuoteSearched = (data: QuoteTrackingData) =>
  sendQuoteEvent(trackingEvents(data.extractionTool).quoteSearched, data);

export const trackQuoteSearchError = (data: QuoteTrackingData) =>
  sendQuoteEvent(trackingEvents(data.extractionTool).quoteSearchError, data);
